import { useCallback, useEffect, useState } from "react";
import { Mail, Power, Send, Square, CheckCircle2, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  apiRecoverySettings, apiSaveRecoverySettings, apiRecoverySessions,
  apiStopRecoverySession, apiRecoveryTest, apiRecoveryAnalytics,
  apiRecoveryProducts, apiRecoveryPreview, errMsg,
} from "../adminApi";
import { EnvironmentBadge } from "../components/EnvironmentBadge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS = {
  ACTIVE: { label: "Attivo", cls: "bg-lime/15 text-lime" },
  RECOVERED: { label: "Recuperato", cls: "bg-emerald-500/15 text-emerald-400" },
  UNSUBSCRIBED: { label: "Disiscritto", cls: "bg-steel/20 text-steel" },
  STOPPED: { label: "Fermato", cls: "bg-destructive/15 text-destructive" },
  EXPIRED: { label: "Scaduto", cls: "bg-carrot/15 text-carrot" },
};

const GRADE_LABELS = { nuovo: "Nuovo", like_new: "Come Nuovo", used: "Usato" };
const GRADE_KEYS = ["nuovo", "like_new", "used"];
const fmtCap = (m) => {
  if (m === null || m === undefined) return "";
  const s = String(m).trim();
  if (!s || s === "0") return "";
  return /GB|TB|MB/i.test(s) ? s : `${s} GB`;
};

export default function CartRecovery() {
  const [settings, setSettings] = useState(null);
  const [resendOk, setResendOk] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [previewProduct, setPreviewProduct] = useState("");
  const [previewTopic, setPreviewTopic] = useState("A");
  const [previewColor, setPreviewColor] = useState("");
  const [previewMemory, setPreviewMemory] = useState("");
  const [previewGrade, setPreviewGrade] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewVariantName, setPreviewVariantName] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadAll = useCallback(() => {
    apiRecoverySettings().then((d) => { setSettings(d.settings); setResendOk(d.resend_configured); }).catch((e) => toast.error(errMsg(e)));
    apiRecoverySessions().then((d) => setSessions(d.sessions)).catch(() => {});
    apiRecoveryAnalytics().then(setAnalytics).catch(() => {});
    apiRecoveryProducts().then((d) => setProducts(d.products || [])).catch(() => {});
  }, []);
  useEffect(loadAll, [loadAll]);

  const selectedProduct = products.find((p) => p.id === previewProduct) || null;
  const colorName = (id) => (selectedProduct?.colors || []).find((c) => c.id === id)?.name || "";
  // Opzioni variante filtrate progressivamente (colore -> capacità -> grado)
  const variantRows = selectedProduct?.variants || [];
  const colorOptions = [...new Map(variantRows.filter((v) => v.color_id).map((v) => [v.color_id, v.color_id])).keys()];
  const memoryOptions = [...new Set(
    variantRows.filter((v) => !previewColor || v.color_id === previewColor)
      .map((v) => v.memory).filter((m) => m !== null && m !== undefined && String(m) !== ""),
  )];
  const gradeOptions = [...new Set(
    variantRows.filter((v) => (!previewColor || v.color_id === previewColor)
      && (!previewMemory || String(v.memory) === String(previewMemory)))
      .map((v) => v.grade).filter(Boolean),
  )];

  // Reset selezione variante al cambio prodotto
  useEffect(() => {
    setPreviewColor(""); setPreviewMemory(""); setPreviewGrade("");
    setPreviewHtml(""); setPreviewVariantName("");
  }, [previewProduct]);

  const genPreview = async () => {
    if (!previewProduct) return toast.error("Scegli un prodotto");
    setPreviewLoading(true);
    try {
      const d = await apiRecoveryPreview(previewProduct, previewTopic, {
        color_id: previewColor || null,
        memory: previewMemory || null,
        grade: previewGrade || null,
      });
      setPreviewHtml(d.html);
      setPreviewVariantName(d.variant_name || "");
    } catch (e) { toast.error(errMsg(e)); } finally { setPreviewLoading(false); }
  };

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const setLegal = (k, v) => setSettings((s) => ({ ...s, legal_block: { ...(s.legal_block || {}), [k]: v } }));
  const setGradeDesc = (g, v) => setSettings((s) => ({ ...s, grade_descriptions: { ...(s.grade_descriptions || {}), [g]: v } }));

  const footerValid = !!(settings?.legal_block?.ragione_sociale || "").trim()
    && !!(settings?.legal_block?.support_email || "").trim();

  const save = async (override) => {
    setSaving(true);
    try {
      const payload = override || settings;
      const d = await apiSaveRecoverySettings(payload);
      setSettings(d.settings);
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const toggleEnabled = async (v) => {
    if (v && !footerValid) {
      toast.error("Compila ragione sociale ed email di supporto (footer) prima di attivare.");
      return;
    }
    set("enabled", v);
    await save({ ...settings, enabled: v });
  };

  const stop = async (id) => {
    try { await apiStopRecoverySession(id); toast.success("Recupero fermato"); loadAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const sendTest = async () => {
    if (!testEmail) return toast.error("Inserisci un indirizzo email");
    try { const d = await apiRecoveryTest(testEmail, "A"); toast.success(`Email di test inviata (${d.subject})`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!settings) return <div className="flex flex-col gap-4 max-w-4xl"><Skeleton className="h-24 rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div>;

  return (
    <div className="flex max-w-4xl flex-col gap-6" data-testid="cart-recovery-page">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface3 text-lime"><Mail className="h-5 w-5" /></div>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Recupero carrelli</h1>
          <p className="mt-1 text-sm text-steel">Email automatiche ai carrelli abbandonati, via Resend.</p>
        </div>
      </div>

      {/* Interruttore globale */}
      <Card className="border-line bg-surface2">
        <CardContent className="flex flex-col gap-3 p-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Power className={`h-5 w-5 ${settings.enabled ? "text-lime" : "text-steel"}`} />
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-chalk">Recupero carrelli {settings.enabled ? "attivo" : "disattivato"}</p>
                  <EnvironmentBadge />
                </div>
                <p className="text-sm text-steel">Quando attivo, i carrelli abbandonati ricevono le email automatiche.</p>
              </div>
            </div>
            <Switch checked={!!settings.enabled} disabled={!footerValid && !settings.enabled}
              onCheckedChange={toggleEnabled} data-testid="recovery-enabled-toggle" />
          </div>
          {!footerValid && (
            <div className="flex items-start gap-2 rounded-lg border border-carrot/40 bg-carrot/10 p-3" data-testid="recovery-footer-warning">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-carrot" />
              <p className="text-sm text-chalk">
                Compila <strong>Ragione sociale</strong> ed <strong>Email di supporto</strong> (sezione footer qui sotto)
                prima di poter attivare il recupero: sono obbligatorie nel footer delle email.
              </p>
            </div>
          )}
          <p className="text-xs text-steel" data-testid="recovery-shared-toggle-note">
            Anteprima e produzione usano lo stesso database: questo interruttore e tutti i dati sono
            condivisi. L'invio reale ai clienti parte comunque solo dall'ambiente di produzione.
          </p>
        </CardContent>
      </Card>

      {/* Stato Resend */}
      <div className={`flex items-start gap-3 rounded-xl border p-4 ${resendOk ? "border-lime/40 bg-lime/10" : "border-carrot/40 bg-carrot/10"}`} data-testid="resend-status">
        {resendOk ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-lime" /> : <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-carrot" />}
        <p className="text-sm text-chalk">{resendOk ? "Connessione Resend attiva." : "Resend non configurato: imposta RESEND_API_KEY nei Secrets."}</p>
      </div>

      {/* Impostazioni */}
      <Card className="border-line bg-surface2">
        <CardContent className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5"><Label>Finestra invio inizio</Label><Input value={settings.send_window_start || ""} onChange={(e) => set("send_window_start", e.target.value)} className="bg-surface" data-testid="rec-window-start" /></div>
          <div className="flex flex-col gap-1.5"><Label>Finestra invio fine</Label><Input value={settings.send_window_end || ""} onChange={(e) => set("send_window_end", e.target.value)} className="bg-surface" data-testid="rec-window-end" /></div>
          <div className="flex flex-col gap-1.5"><Label>Orari di invio (slot, separati da virgola)</Label><Input value={(settings.slots || []).join(", ")} onChange={(e) => set("slots", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} className="bg-surface" data-testid="rec-slots" /></div>
          <div className="flex flex-col gap-1.5"><Label>Ordine topic</Label><Input value={(settings.topic_order || []).join(", ")} onChange={(e) => set("topic_order", e.target.value.split(",").map((x) => x.trim().toUpperCase()).filter(Boolean))} className="bg-surface" data-testid="rec-topics" /></div>
          <div className="flex flex-col gap-1.5"><Label>Giorni massimi</Label><Input type="number" value={settings.max_days ?? ""} onChange={(e) => set("max_days", parseInt(e.target.value) || 7)} className="bg-surface" /></div>
          <div className="flex flex-col gap-1.5"><Label>Ritardo primo invio (minuti)</Label><Input type="number" value={settings.first_delay_minutes ?? ""} onChange={(e) => set("first_delay_minutes", parseInt(e.target.value) || 60)} className="bg-surface" /></div>
          <div className="flex flex-col gap-1.5"><Label>Giorni di reso</Label><Input type="number" value={settings.return_window_days ?? ""} onChange={(e) => set("return_window_days", parseInt(e.target.value) || 30)} className="bg-surface" /></div>
          <div className="flex flex-col gap-1.5"><Label>Mesi di garanzia</Label><Input type="number" value={settings.warranty_months ?? ""} onChange={(e) => set("warranty_months", parseInt(e.target.value) || 12)} className="bg-surface" /></div>
          <div className="flex flex-col gap-1.5 sm:col-span-2"><Label>Ragione sociale (footer legale)</Label><Input value={settings.legal_block?.ragione_sociale || ""} onChange={(e) => set("legal_block", { ...settings.legal_block, ragione_sociale: e.target.value })} className="bg-surface" /></div>
          <div className="flex flex-col gap-1.5"><Label>P.IVA</Label><Input value={settings.legal_block?.piva || ""} onChange={(e) => set("legal_block", { ...settings.legal_block, piva: e.target.value })} className="bg-surface" /></div>
          <div className="flex flex-col gap-1.5"><Label>Email di supporto</Label><Input value={settings.legal_block?.support_email || ""} onChange={(e) => setLegal("support_email", e.target.value)} className="bg-surface" data-testid="rec-support-email" /></div>
          <div className="flex flex-col gap-1.5 sm:col-span-2"><Label>Sede (facoltativa)</Label><Input value={settings.legal_block?.sede || ""} onChange={(e) => setLegal("sede", e.target.value)} className="bg-surface" data-testid="rec-sede" /></div>

          <div className="sm:col-span-2 border-t border-line pt-4">
            <p className="text-sm font-medium text-chalk">Descrizione dei gradi (facoltativa)</p>
            <p className="text-xs text-steel">Usata solo dal topic B per spiegare cosa significa ogni grado. Se vuota, non ne parla.</p>
          </div>
          {GRADE_KEYS.map((g) => (
            <div key={g} className="flex flex-col gap-1.5 sm:col-span-2">
              <Label>{GRADE_LABELS[g]}</Label>
              <Textarea rows={2} value={settings.grade_descriptions?.[g] || ""} onChange={(e) => setGradeDesc(g, e.target.value)} className="bg-surface" placeholder={`Cosa significa in pratica "${GRADE_LABELS[g]}"`} data-testid={`rec-grade-desc-${g}`} />
            </div>
          ))}
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label>Processo di ricondizionamento (facoltativo)</Label>
            <Textarea rows={3} value={settings.refurb_process || ""} onChange={(e) => set("refurb_process", e.target.value)} className="bg-surface" placeholder="Come lavoriamo i dispositivi (usato dal topic B, se compilato)" data-testid="rec-refurb-process" />
          </div>

          <div className="sm:col-span-2 flex justify-end"><Button variant="lime" disabled={saving} onClick={() => save()} data-testid="rec-save">Salva impostazioni</Button></div>
        </CardContent>
      </Card>

      {/* Invia email di test */}
      <Card className="border-line bg-surface2">
        <CardContent className="flex flex-wrap items-end gap-3 p-5">
          <div className="flex flex-1 flex-col gap-1.5"><Label>Invia email di test a</Label><Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="tua@email.it" className="bg-surface" data-testid="rec-test-email" /></div>
          <Button variant="secondary" onClick={sendTest} data-testid="rec-test-send"><Send className="h-4 w-4" /> Invia test</Button>
        </CardContent>
      </Card>

      {/* Anteprima email per topic (nessun invio, nessuna sessione, non in analytics) */}
      <Card className="border-line bg-surface2">
        <CardContent className="flex flex-col gap-4 p-5" data-testid="rec-preview-card">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex min-w-[220px] flex-1 flex-col gap-1.5">
              <Label>Prodotto per l'anteprima</Label>
              <Select value={previewProduct} onValueChange={setPreviewProduct}>
                <SelectTrigger className="bg-surface" data-testid="rec-preview-product"><SelectValue placeholder="Scegli un prodotto pubblicato" /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Topic</Label>
              <div className="flex gap-1">
                {["A", "B", "C"].map((t) => (
                  <Button key={t} size="sm" variant={previewTopic === t ? "lime" : "secondary"}
                    onClick={() => setPreviewTopic(t)} data-testid={`rec-preview-topic-${t}`}>{t}</Button>
                ))}
              </div>
            </div>
            <Button variant="secondary" disabled={previewLoading} onClick={genPreview} data-testid="rec-preview-generate">
              {previewLoading ? "Generazione…" : "Genera anteprima"}
            </Button>
          </div>

          {/* Selezione variante (colore / capacità / grado) */}
          {selectedProduct && (
            <div className="flex flex-wrap items-end gap-3" data-testid="rec-preview-variant">
              {colorOptions.length > 0 && (
                <div className="flex min-w-[160px] flex-col gap-1.5">
                  <Label>Colore</Label>
                  <Select value={previewColor || "__any"} onValueChange={(v) => { setPreviewColor(v === "__any" ? "" : v); setPreviewMemory(""); setPreviewGrade(""); }}>
                    <SelectTrigger className="bg-surface" data-testid="rec-preview-color"><SelectValue placeholder="Qualsiasi" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__any">Qualsiasi</SelectItem>
                      {colorOptions.map((cid) => <SelectItem key={cid} value={cid}>{colorName(cid) || "Colore"}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {memoryOptions.length > 0 && (
                <div className="flex min-w-[140px] flex-col gap-1.5">
                  <Label>Capacità</Label>
                  <Select value={previewMemory || "__any"} onValueChange={(v) => { setPreviewMemory(v === "__any" ? "" : v); setPreviewGrade(""); }}>
                    <SelectTrigger className="bg-surface" data-testid="rec-preview-memory"><SelectValue placeholder="Qualsiasi" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__any">Qualsiasi</SelectItem>
                      {memoryOptions.map((m) => <SelectItem key={String(m)} value={String(m)}>{fmtCap(m)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex min-w-[150px] flex-col gap-1.5">
                <Label>Grado</Label>
                <Select value={previewGrade || "__any"} onValueChange={(v) => setPreviewGrade(v === "__any" ? "" : v)}>
                  <SelectTrigger className="bg-surface" data-testid="rec-preview-grade"><SelectValue placeholder="Qualsiasi" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any">Qualsiasi</SelectItem>
                    {gradeOptions.map((g) => <SelectItem key={g} value={g}>{GRADE_LABELS[g] || g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {previewVariantName && (
            <p className="text-xs text-lime" data-testid="rec-preview-variant-name">Variante: {previewVariantName}</p>
          )}
          <p className="text-xs text-steel">L'anteprima genera l'email con un prodotto reale ma non invia nulla, non crea sessioni e non viene conteggiata.</p>
          {previewHtml && (
            <iframe title="Anteprima email" srcDoc={previewHtml} data-testid="rec-preview-frame"
              className="h-[520px] w-full rounded-lg border border-line bg-white" sandbox="" />
          )}
        </CardContent>
      </Card>

      {/* Analytics */}
      {analytics && (
        <div className="grid grid-cols-3 gap-3" data-testid="rec-analytics">
          <Card className="border-line bg-surface2"><CardContent className="p-4"><p className="text-xs text-steel">Inviate</p><p className="font-display text-2xl text-chalk">{analytics.totals.sent}</p></CardContent></Card>
          <Card className="border-line bg-surface2"><CardContent className="p-4"><p className="text-xs text-steel">Recuperati</p><p className="font-display text-2xl text-chalk">{analytics.totals.recovered}</p></CardContent></Card>
          <Card className="border-line bg-surface2"><CardContent className="p-4"><p className="text-xs text-steel">Fatturato recuperato</p><p className="font-display text-2xl text-chalk">{analytics.totals.revenue}</p></CardContent></Card>
        </div>
      )}

      {/* Sessioni */}
      <Card className="border-line bg-surface2">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-line hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wide text-steel">Carrello</TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-steel">Stato</TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-steel">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-steel">Prossimo invio</TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-steel">Ultimo topic</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="py-10 text-center text-steel">Nessuna sessione di recupero.</TableCell></TableRow>
              ) : sessions.map((s) => {
                const st = STATUS[s.status] || { label: s.status, cls: "bg-steel/20 text-steel" };
                return (
                  <TableRow key={s.id} className="border-line" data-testid="rec-session-row">
                    <TableCell className="text-sm text-chalk">{s.cart_id}</TableCell>
                    <TableCell><Badge className={`${st.cls} border-0`}>{st.label}</Badge></TableCell>
                    <TableCell className="text-sm text-steel">{s.sent_label}</TableCell>
                    <TableCell className="text-sm text-steel">{s.next_send_at ? new Date(s.next_send_at).toLocaleString("it-IT") : "—"}</TableCell>
                    <TableCell className="text-sm text-steel">{s.last_topic || "—"}</TableCell>
                    <TableCell className="text-right">
                      {s.status === "ACTIVE" && (
                        <Button size="sm" variant="ghost" onClick={() => stop(s.id)} data-testid="rec-stop"><Square className="h-4 w-4" /> Ferma</Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
